// utils/csvExporter.js
import { formatDateForCSV } from './dateFormatter';

/**
 * Export table data to CSV file.
 * `tz` (optional): when set, datetime cells are converted to that timezone
 * and the abbreviation is appended inside each cell.
 */
export const exportToCSV = (rows, columns, filename = 'report.csv', tz) => {
  // Filter out columns we don't want in CSV
  const columnsToExport = columns.filter(
    (col) => !['recording_path', 'answer_at', 'uniqueid', 'hangupby'].includes(col.field)
  );

  // Prepare rows with formatted dates
  const rowsToExport = rows.map((row) => {
    const clonedRow = { ...row };

    if (clonedRow.start_at) {
      clonedRow.start_at = formatDateForCSV(clonedRow.start_at, tz);
    }
    if (clonedRow.end_at) {
      clonedRow.end_at = formatDateForCSV(clonedRow.end_at, tz);
    }

    const { recording_path, answer_at, uniqueid, ...filteredRow } = clonedRow;
    return { id: clonedRow.id, ...filteredRow };
  });

  // Create CSV data
  const csvData = rowsToExport.map((row) =>
    columnsToExport.map((col) => row[col.field] || "")
  );

  // Generate CSV content with BOM for UTF-8
  const header = columnsToExport.map((col) => col.headerName).join(",") + "\n";
  const rowsCsv = csvData.map((row) => row.join(",")).join("\n");
  const csvWithBOM = "﻿" + header + rowsCsv;

  // Download file
  const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Export modal data to CSV.
 * `tz` (optional): same semantics as exportToCSV.
 */
export const exportModalDataToCSV = (callDetails, buyerDialogs, filename = 'call_details.csv', tz) => {
  // Prepare call details data
  const callDetailsData = [
    {
      'Caller ID': callDetails.caller_id_number,
      'DID Number': callDetails.did_tfn,
      'Forwarded': callDetails.forwarded_number,
      'Status': callDetails.hangup_reason,
      'Call Status': callDetails.call_result,
      'Buyer Name': callDetails.buyer_name,
      'Campaign Name': callDetails.campaign_name,
      'Duration': callDetails.duration,
      'Call Answer': formatDateForCSV(callDetails.answer_at, tz),
      'Call Start': formatDateForCSV(callDetails.start_at, tz),
      'Call End': formatDateForCSV(callDetails.end_at, tz),
      'Hangup By': callDetails.hangupby,
      'Unique ID': callDetails.uniqueid,
    }
  ];

  // Prepare buyer dialogs data
  const buyerDialogsData = buyerDialogs.map(dialog => ({
    'Unique Id': dialog.uniqueid,
    'Buyer Name': dialog.buyer_name,
    'Buyer Number': dialog.buyer_number,
    'Actual Timeout': dialog.actual_timeout,
    'Timeout':dialog.timeout,
    'Status': dialog.status,
    'Created At': formatDateForCSV(dialog.created_at, tz),
  }));

  // Combine both datasets with headers
  const content = [
    '=== CALL DETAILS ===',
    ...convertToCSV(callDetailsData),
    '',
    '=== BUYER DIAL LOGS ===',
    ...convertToCSV(buyerDialogsData),
  ].join('\n');

  const blob = new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Convert array of objects to CSV format
 */
const convertToCSV = (data) => {
  if (data.length === 0) return [];

  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(',')];

  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header] || '';
      return `"${String(value).replace(/"/g, '""')}"`;
    });
    csvRows.push(values.join(','));
  }

  return csvRows;
};
